import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import { useHolidays, useWithdrawApprovedLeave } from "@/api/queries";
import type { LeaveRequest } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { toast } from "@/components/ui/toast";
import { eachDateInRange, holidayMap, isRestDay } from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";

/**
 * Handing back leave that was already approved.
 *
 * Withdrawal is per DAY, and this is where that becomes visible. A one-day
 * request has nothing to choose and goes straight to a confirmation; a multi-day
 * one lists its working days so somebody who worked the Tuesday of a five-day
 * leave hands back the Tuesday, not the week.
 *
 * There is no approval step. The days return to the balance on submit and the
 * approver is told afterwards — see the backend route for why waiting would be
 * worse than acting.
 *
 * What this dialog has to be honest about, and says plainly at the bottom: a
 * withdrawn day is no longer covered by anything. If it was not worked and gets
 * no attendance, it becomes an unexplained absence. That is correct, and it is
 * the reason withdrawal is normally reached from the attendance card at the
 * moment somebody is recording the day they worked.
 */
export function WithdrawApprovedDialog({
  request,
  onClose,
}: {
  request: LeaveRequest;
  onClose: () => void;
}) {
  const holidays = useHolidays();
  const withdraw = useWithdrawApprovedLeave();

  const holidaySet = useMemo(() => new Set(holidayMap(holidays.data).keys()), [holidays.data]);
  const alreadyWithdrawn = useMemo(
    () => new Set(request.withdrawn_days ?? []),
    [request.withdrawn_days],
  );

  // Only working days can be handed back — a Sunday inside the span was never
  // charged, so there is nothing there to return.
  const days = useMemo(
    () =>
      eachDateInRange(request.start_date, request.end_date).filter(
        (date) => !isRestDay(date, holidaySet),
      ),
    [request.start_date, request.end_date, holidaySet],
  );
  const live = days.filter((date) => !alreadyWithdrawn.has(date));

  const singleDay = live.length <= 1;

  /*
    What the person has PICKED. What the dialog acts on is derived below.

    These two used to be seeded from `singleDay`, which depends on `live`, which
    depends on the holiday list arriving. Open the dialog before that lands on a
    span containing a company holiday and `singleDay` was false, so scope froze
    at "day" with nothing selected — then the holiday arrived, `live` shrank to
    one, and both the scope radios and the day picker hid behind `!singleDay`
    while `canSubmit` stayed false. A dialog with no controls and a dead button,
    escapable only by closing and reopening it.
  */
  const [pickedScope, setPickedScope] = useState<"day" | "all">("day");
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  // "All of it" is a real choice, not the absence of one, so it is a value
  // rather than a null selection. A single remaining day has no choice to make.
  const scope: "day" | "all" = singleDay ? "all" : pickedScope;
  // A day that is no longer withdrawable (the set shrank under it) is not a
  // selection — holding on to it would submit a date the server rejects.
  const selected = singleDay
    ? (live[0] ?? null)
    : pickedDay && live.includes(pickedDay)
      ? pickedDay
      : null;

  function submit() {
    const body =
      scope === "all" || !selected ? { id: request.id } : { id: request.id, date: selected };
    withdraw.mutate(body, {
      onSuccess: (result) => {
        toast.success(
          result.fullyWithdrawn
            ? "Leave withdrawn. The days are back on your balance."
            : `${result.withdrawnDates.length} day withdrawn. It's back on your balance.`,
        );
        onClose();
      },
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : "Could not withdraw that."),
    });
  }

  // Until the holidays are in, `live` is provisional and so is everything read
  // off it — there is nothing honest to submit yet.
  const canSubmit = !holidays.isLoading && (scope === "all" || Boolean(selected));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Withdraw approved leave"
          description={`${leaveLabel(request.leave_type)} · ${
            request.start_date === request.end_date
              ? formatDate(request.start_date)
              : `${formatDate(request.start_date)} to ${formatDate(request.end_date)}`
          }`}
        />

        <DialogBody className="flex flex-col gap-4">
          {!singleDay && (
            <div className="flex flex-col gap-2">
              <ScopeOption
                checked={scope === "day"}
                onSelect={() => setPickedScope("day")}
                label="One day of it"
                hint="The rest of the leave stays approved."
              />
              <ScopeOption
                checked={scope === "all"}
                onSelect={() => setPickedScope("all")}
                label={`All ${live.length} remaining days`}
                hint="The whole request is handed back."
              />
            </div>
          )}

          {scope === "day" && !singleDay && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted">Which day did you work?</p>
              <div className="flex flex-wrap gap-1.5">
                {live.map((date) => (
                  <button
                    key={date}
                    type="button"
                    onClick={() => setPickedDay(date)}
                    className={cn(
                      "rounded-lg border px-2.5 py-1.5 font-mono text-xs transition-colors",
                      selected === date
                        ? "border-accent bg-accent-wash text-accent"
                        : "border-line text-ink-soft hover:border-line-strong hover:text-ink",
                    )}
                  >
                    {formatDate(date, "d MMM")}
                  </button>
                ))}
              </div>
            </div>
          )}

          {alreadyWithdrawn.size > 0 && (
            <p className="text-xs text-muted">
              {alreadyWithdrawn.size} day{alreadyWithdrawn.size === 1 ? "" : "s"} of this request{" "}
              {alreadyWithdrawn.size === 1 ? "has" : "have"} already been withdrawn.
            </p>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-line bg-surface-sunken/50 px-3 py-2.5">
            <Icon name="info" size={16} className="mt-0.5 shrink-0 text-faint" />
            <p className="text-xs leading-relaxed text-ink-soft">
              The days go straight back on your balance and your approver is notified. A withdrawn
              day is no longer covered by leave — if you don&rsquo;t record attendance for it, it
              counts as an unexplained absence.
            </p>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={withdraw.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={withdraw.isPending}
            disabled={!canSubmit}
          >
            {scope === "all" ? "Withdraw all" : "Withdraw this day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeOption({
  checked,
  onSelect,
  label,
  hint,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  hint: string;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors",
        checked ? "border-accent bg-accent-wash/50" : "border-line hover:border-line-strong",
      )}
    >
      <input
        type="radio"
        name="withdraw-scope"
        checked={checked}
        onChange={onSelect}
        className="mt-0.5 h-4 w-4 accent-accent"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        <span className="block text-xs text-muted">{hint}</span>
      </span>
    </label>
  );
}
